import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from '../GlassCard';
import { GlassOptionCard } from '../GlassOptionCard';
import { GlassCTA } from '../GlassCTA';

interface GoalItem {
  text: string;
  phase: string;
}

interface PriorityStepProps {
  goals: GoalItem[];
  onComplete: (orderedGoals: GoalItem[]) => void;
}

export function PriorityStep({ goals, onComplete }: PriorityStepProps) {
  // Tap-to-rank: each tap assigns the next priority number
  const [ranked, setRanked] = useState<number[]>([]);

  const handleTap = useCallback(
    (index: number) => {
      setRanked((prev) => {
        const existing = prev.indexOf(index);
        if (existing >= 0) {
          // Deselect: remove this and all after it
          return prev.slice(0, existing);
        }
        return [...prev, index];
      });
    },
    []
  );

  const allRanked = ranked.length === goals.length;

  const handleComplete = () => {
    const orderedGoals = ranked.map((idx) => goals[idx]);
    onComplete(orderedGoals);
  };

  return (
    <GlassCard>
      <div className="flex flex-col items-center text-center">
        <h1
          className="font-display text-[28px] font-semibold text-ink tracking-tight"
        >
          If you could only move one of these forward this week, which would it
          be?
        </h1>
        <p
          className="mt-3 text-base"
          style={{ color: 'hsl(var(--ink-2))' }}
        >
          Tap in order of priority.
        </p>

        <div className="mt-8 flex w-full flex-col gap-3">
          {goals.map((goal, i) => {
            const rankPosition = ranked.indexOf(i);
            const isRanked = rankPosition >= 0;

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 + i * 0.06 }}
              >
                <GlassOptionCard
                  selected={isRanked}
                  onClick={() => handleTap(i)}
                  className="flex items-center gap-4"
                >
                  <motion.span
                    animate={{ scale: isRanked ? [1, 1.15, 1] : 1 }}
                    transition={{ duration: 0.2 }}
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-mono text-sm font-semibold transition-colors duration-200 ${
                      isRanked ? 'bg-ink text-paper-raised' : 'border border-line bg-paper-sunken text-ink-3'
                    }`}
                  >
                    {isRanked ? rankPosition + 1 : ''}
                  </motion.span>
                  <span
                    className={`flex-1 text-left text-base font-medium transition-colors duration-200 ${isRanked ? 'text-ink' : 'text-ink-2'}`}
                  >
                    {goal.text}
                  </span>
                </GlassOptionCard>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10">
          <GlassCTA onClick={handleComplete} disabled={!allRanked}>
            Build my boards
          </GlassCTA>
        </div>
      </div>
    </GlassCard>
  );
}
